import React, { Component, createRef } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import classnames from 'classnames'
import Loader from '@app/components/ui/placeholders/Loader'

class AnswerPresenter extends Component {
  constructor(props) {
    super(props)
    this.container = createRef()
    this.scrollHeight = 250
    this.retryCount = 0
    this.maxRetries = 5
    this.retryTimeout = null
    this.state = { showLoader: true }

    this.handleResize = this.handleResize.bind(this)
    this.retainPreferredHeight = this.retainPreferredHeight.bind(this)
    this.fetchContent = this.fetchContent.bind(this)
    this.retryFetch = this.retryFetch.bind(this)
  }

  get width() { return 620 }

  componentDidMount() {
    this.fetchContent();
  }

  async fetchContent() {
    try {
      const { url } = this.props;
      const response = await fetch(url);
      
      if (response.ok) {
        const html = await response.text();
        this.initializeFrame(html);
        this.setState({ showLoader: false });
        this.retryCount = 0;
        if (this.retryTimeout) {
          clearTimeout(this.retryTimeout);
          this.retryTimeout = null;
        }
      } else if (response.status === 404 && this.retryCount < this.maxRetries) {
        this.scheduleRetry();
      } else {
        this.setState({ showLoader: false });
        console.warn('Failed to load content after max retries');
      }
    } catch (error) {
      console.warn('Error fetching content:', error);
      if (this.retryCount < this.maxRetries) {
        this.scheduleRetry();
      } else {
        this.setState({ showLoader: false });
      }
    }
  }

  initializeFrame(html) {
    const { className, ...rest } = this.props
    
    const blob = new Blob([html], { type: 'text/html' });
    const blobUrl = URL.createObjectURL(blob);
    
    const config = _.defaultsDeep(rest, {
      width: this.width,
      height: this.scrollHeight,
      style: {verticalAlign: 'top'},
      class: "uchi-raw-frame",
      resizeOnLoad: true,
      resizeCallback: this.retainPreferredHeight,
      onload: () => {
        URL.revokeObjectURL(blobUrl);
      }
    })

    this.frame = new UCHI.UchiRawFrame(this.container.current, blobUrl, config);

    window.addEventListener('resize', this.handleResize);
    this.handleResize();
  }

  componentWillUnmount() {
    if (this.frame) {
      window.removeEventListener('resize', this.handleResize);
    }
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }
  }

  retainPreferredHeight(scrollHeight) {
    this.scrollHeight = scrollHeight
    this.handleResize()
  }

  handleResize() {
    const maxWidth = this.container.current.parentNode.clientWidth
    const scale = maxWidth / this.width

    this.setScale(scale >= 1 ? 1 : scale)
  }

  setScale(ratio) {
    const element = this.container.current

    element.style.height = `${this.scrollHeight * ratio}px`
    element.style.width = `${this.width * ratio}px`
    element.style.overflow = `hidden`

    const iframe = this.frame.view
    const property = `scale(${ratio})`

    iframe.style.webkitTransform = property
    iframe.style.MozTransform = property
    iframe.style.msTransform = property
    iframe.style.OTransform = property
    iframe.style.transform = property
    iframe.style.width = `${100 / ratio}%`
    iframe.style.height = `${100 / ratio}%`
    iframe.style.transformOrigin = '0 0';
    iframe.style.WebkitTransformOrigin = '0 0';
  }

  scheduleRetry() {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }
    
    const delay = Math.min(500 * Math.pow(2, this.retryCount), 8000);
    
    this.retryTimeout = setTimeout(() => {
      this.retryFetch();
    }, delay);
  }

  retryFetch() {
    this.retryCount++;
    if (this.retryCount <= this.maxRetries) {
      this.fetchContent();
    }
  }

  render() {
    const { url, className, ...rest } = this.props
    const classes = classnames('answer-presenter', className)

    return (
      <div className={classes} {...rest}>
        <div 
          className="answer-presenter__wrapper" 
          ref={this.container} 
          style={{ display: this.state.showLoader ? 'none' : 'block' }}
        />
        {this.state.showLoader && <Loader />}
      </div>
    )
  }
}

AnswerPresenter.propTypes = {
  url: PropTypes.string.isRequired
}

export default AnswerPresenter
